import { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Autocomplete,
  TextField,
  IconButton,
} from "@mui/material";
import {
  FileText,
  Minus,
  Maximize2,
  X,
  Search,
  AlertCircle,
  Minimize2,
  Maximize,
} from "lucide-react";
import { DocProducerContent } from "./DocProducerContent";
import { QueryContact } from "@/api/queries/types";
import { Contact } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { cn } from "@/lib/utils";

interface DocProducerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  openContact?: Contact | QueryContact | null;
  contacts: QueryContact[];
  shouldRestore?: boolean;
  onRestoreComplete?: () => void;
  contactIdFromUrl?: string | null;
}
export const DocProducerDialog = ({
  isOpen,
  onClose,
  openContact,
  contacts,
  shouldRestore,
  onRestoreComplete,
  contactIdFromUrl,
}: DocProducerDialogProps) => {
  const [position, setPosition] = useState({
    x: 50,
    y: 50,
  });
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);

  const [selectedContact, setSelectedContact] = useState<QueryContact | null>(
    null
  );

  useEffect(() => {
    if (openContact) {
      const contactOption =
        contacts?.find((c) => c.id === openContact.id) || null;
      setSelectedContact(contactOption);
    }
  }, [contacts, openContact]);

  // Handle restore from minimized when dialog is already open
  useEffect(() => {
    if (shouldRestore && isMinimized) {
      setIsMinimized(false);
      onRestoreComplete?.();
    }
  }, [shouldRestore, isMinimized, onRestoreComplete]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      isDraggingRef.current = true;
      dragStart.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [position]
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;

    // Allow dragging the dialog past the left edge (and partially off the
    // right edge) — useful when the viewport is small and the user needs
    // to reach the title-bar buttons that are anchored on the right.
    // We only keep at least ~100px visible on the right and the full title
    // bar visible vertically so the dialog can always be grabbed again.
    const maxX = window.innerWidth - 100;
    const maxY = window.innerHeight - 100;

    setPosition({
      x: Math.min(newX, maxX),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
  }, []);

  const handleMinimize = useCallback(() => {
    setIsMinimized(true);
    setIsMaximized(false);
  }, []);

  const handleMaximize = useCallback(() => {
    setIsMaximized(!isMaximized);
    setIsMinimized(false);
  }, [isMaximized]);

  // Lock/unlock body scroll when maximized
  useEffect(() => {
    if (isMaximized) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMaximized]);

  // Reset body scroll when dialog closes while maximized
  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "";
    }
  }, [isOpen]);

  const handleRestore = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Detect when resize is happening
  useEffect(() => {
    const handleResizeStart = () => setIsResizing(true);
    const handleResizeEnd = () => setIsResizing(false);

    const observer = new MutationObserver(() => {
      // Check if user is actively resizing
      if (document.activeElement?.tagName === "BODY") {
        const isCurrentlyResizing =
          document.body.style.cursor.includes("resize");
        if (isCurrentlyResizing) {
          setIsResizing(true);
        }
      }
    });

    document.addEventListener("mousedown", handleResizeStart);
    document.addEventListener("mouseup", handleResizeEnd);

    return () => {
      document.removeEventListener("mousedown", handleResizeStart);
      document.removeEventListener("mouseup", handleResizeEnd);
      observer.disconnect();
    };
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      hideBackdrop
      disableEnforceFocus
      disableScrollLock
      PaperProps={{
        sx: {
          position: "fixed",
          transition:
            isDragging || isResizing
              ? "none"
              : "left 0.3s ease-in-out, top 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out, min-width 0.3s ease-in-out, min-height 0.3s ease-in-out, max-width 0.3s ease-in-out, max-height 0.3s ease-in-out",
          m: 0,
          ...(isMinimized
            ? {
                // Park at bottom-right using left/top so the transition can
                // interpolate position alongside size.
                left: "calc(100vw - 366px) !important",
                top: "calc(100vh - 69px) !important",
                right: "auto !important",
                bottom: "auto !important",
                width: "350px !important",
                minWidth: "350px !important",
                maxWidth: "350px !important",
                height: "53px !important",
                minHeight: "53px !important",
                maxHeight: "53px !important",
                resize: "none !important",
                overflow: "hidden",
              }
            : isMaximized
            ? {
                left: "0 !important",
                top: "0 !important",
                width: "100vw !important",
                height: "100vh !important",
                maxWidth: "100vw !important",
                maxHeight: "100vh !important",
                resize: "none !important",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }
            : {
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: "80vw",
                height: "90vh",
                maxWidth: "none",
                maxHeight: "none",
                resize: "both",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minWidth: "400px",
                minHeight: "300px",
              }),
        },
      }}
      sx={{
        pointerEvents: "none",
        "& .MuiDialog-container": {
          pointerEvents: "none",
          // Always anchor top-left — the Paper itself is absolutely positioned
          // via left/top, so flipping the container alignment would snap the
          // dialog across the screen before the size transition can play.
          alignItems: "flex-start",
          justifyContent: "flex-start",
        },
        "& .MuiPaper-root": {
          pointerEvents: "auto",
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <div
        onMouseDown={(e) => {
          // Don't drag if clicking on autocomplete or buttons
          const target = e.target as HTMLElement;
          if (
            !isMinimized &&
            !isMaximized &&
            !target.closest(".MuiAutocomplete-root") &&
            !target.closest("button")
          ) {
            handleMouseDown(e);
          }
        }}
        className={cn(
          "px-3 py-2 border-b flex items-center gap-2 h-[56px] select-none w-full",
          { "cursor-move": !isMinimized && !isMaximized }
        )}
      >
        <div className="size-9 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center flex-shrink-0">
          <FileText
            className="size-4 text-blue-600 dark:text-blue-400"
            strokeWidth={2.5}
          />
        </div>
        {isMinimized && (
          <div className="font-medium text-sm w-full overflow-hidden whitespace-nowrap text-ellipsis">
            {selectedContact ? selectedContact.name : "Doc Producer"}
          </div>
        )}
        {!isMinimized && (
          <div className="flex items-center gap-2 flex-1 overflow-hidden">
            {/* Search icon and autocomplete on the left */}
            <div className="flex items-center gap-2">
              {!showSearch && selectedContact && (
                <div className="font-semibold text-2xl uppercase tracking-wide flex items-center">
                  {selectedContact.name}
                </div>
              )}
              {!showSearch ? (
                <Button
                  size="sm"
                  variant="icon"
                  onClick={() => setShowSearch(true)}
                  title="Search contact"
                >
                  <Search className="size-4" />
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <div
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      width: showSearch ? "300px" : "0px",
                      opacity: showSearch ? 1 : 0,
                    }}
                  >
                    <Autocomplete
                      value={selectedContact}
                      onChange={(_, newValue) => {
                        setSelectedContact(newValue);
                        setShowSearch(false);
                      }}
                      onBlur={() => setShowSearch(false)}
                      options={contacts}
                      getOptionLabel={(option) => option.name}
                      filterOptions={(options, state) => {
                        const filtered = options.filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(state.inputValue.toLowerCase())
                        );
                        return filtered.slice(0, 30);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search contact..."
                          size="small"
                          autoFocus
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <Search className="size-4 text-gray-400 mr-1" />
                            ),
                          }}
                        />
                      )}
                      size="small"
                      sx={{
                        width: 300,
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgba(0, 0, 0, 0.03)",
                          borderRadius: "6px",
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                            boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.1)",
                          },
                        },
                      }}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="icon"
                    onClick={() => setShowSearch(false)}
                    title="Close search"
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-end gap-1 flex-shrink-0">
          {isMinimized ? (
            <Button
              size="sm"
              onClick={handleRestore}
              title="Restore"
              variant="icon"
            >
              <Maximize2 className="size-4 stroke-primary" />
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                onClick={handleMinimize}
                title="Minimize"
                variant="icon"
              >
                <Minus className="size-4 stroke-primary" />
              </Button>
              <Button
                size="sm"
                onClick={handleMaximize}
                title={isMaximized ? "Restore" : "Maximize"}
                variant="icon"
              >
                {isMaximized ? (
                  <Minimize2 className="size-4 stroke-primary" />
                ) : (
                  <Maximize className="size-4 stroke-primary" />
                )}
              </Button>
            </>
          )}
          <Button size="sm" onClick={onClose} title="Close" variant="icon">
            <X className="size-4 stroke-primary" />
          </Button>
        </div>
      </div>

      <DialogContent sx={{ flex: 1, p: 0, overflow: "hidden" }}>
        <div className="w-full h-full">
          <DocProducerContent selectedContact={selectedContact} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
